import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, FileText, ArrowRight, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";

interface PatientTimelineProps {
  patientId: string;
  currentRecordId?: string;
}

const visitTypeLabels: Record<string, string> = {
  consultation: "Consulta",
  follow_up: "Retorno",
  emergency: "Emergência",
  procedure: "Procedimento",
  other: "Outro",
};

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  archived: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
};

const statusLabels: Record<string, string> = {
  draft: "Rascunho",
  completed: "Concluído",
  archived: "Arquivado",
};

export function PatientTimeline({ patientId, currentRecordId }: PatientTimelineProps) {
  const navigate = useNavigate();

  const { data: records, isLoading } = useQuery({
    queryKey: ["patient-clinical-records", patientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clinical_records")
        .select("id, visit_date, visit_type, chief_complaint, status, diagnosis")
        .eq("patient_id", patientId)
        .order("visit_date", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data || [];
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Histórico de Atendimentos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!records || records.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Histórico de Atendimentos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {records.map((record, index) => {
            const isCurrent = record.id === currentRecordId;
            const diagnosisData = record.diagnosis as { records?: Array<{ type: string; description?: string }> } | null;
            const primaryDiagnosis = diagnosisData?.records?.find(
              (d) => d.type === "primary"
            );

            return (
              <div
                key={record.id}
                className={`relative pl-8 pb-4 ${
                  index < records.length - 1 ? "border-l-2 border-border" : ""
                }`}
              >
                {/* Timeline dot */}
                <div
                  className={`absolute left-0 top-1 w-3 h-3 rounded-full border-2 ${
                    isCurrent
                      ? "bg-primary border-primary"
                      : "bg-background border-border"
                  }`}
                />

                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-sm">
                          {format(new Date(record.visit_date), "dd 'de' MMMM 'de' yyyy", {
                            locale: ptBR,
                          })}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {visitTypeLabels[record.visit_type || "consultation"]}
                        </Badge>
                        <Badge className={`text-xs ${statusColors[record.status || "draft"]}`}>
                          {statusLabels[record.status || "draft"]}
                        </Badge>
                      </div>
                      {record.chief_complaint && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {record.chief_complaint}
                        </p>
                      )}
                      {primaryDiagnosis && (
                        <p className="text-xs text-muted-foreground mt-1">
                          <span className="font-medium">Diagnóstico:</span>{" "}
                          {primaryDiagnosis.description}
                        </p>
                      )}
                    </div>
                    {!isCurrent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/app/aplicativo/clinicalrecord/${record.id}`)}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {records.length >= 10 && (
          <div className="mt-4 text-center">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/app/aplicativo/clinicalrecord?patientId=${patientId}`)}
            >
              Ver todos os atendimentos
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

